import React, { useCallback, useEffect, useRef, useState } from "react";
import * as api from "./electricalApi";
import { blobUrl, revoke } from "./offline/photos";
import "./ElectricalPhotos.css";

/**
 * Photo capture. Modeled on Csa/CsaPhotos.js.
 *
 * Two ways to take a photo, because they match different moments on site and
 * both are needed:
 *
 *   Per checkpoint — the auditor is already on the checkpoint, sees the
 *                    thing, taps camera. Clean data, no second step.
 *   Camera roll    — they are walking, see six things in two minutes, shoot
 *                    them all and sort it out sitting down.
 *
 * An unassigned photo is the risk: it has no place in the sync model and
 * still consumes quota. So submit blocks while any is unassigned — assign or
 * discard, explicitly.
 */

/* ------------------------------------------------------------------ */
/* a stored blob, rendered                                             */
/* ------------------------------------------------------------------ */

function PhotoImg({ photo, className, onClick }) {
  const [src, setSrc] = useState(null);

  useEffect(() => {
    if (photo.thumb) {
      const url = blobUrl(photo.thumb);
      setSrc(url);
      return () => revoke(url);
    }
    setSrc(photo.thumb_url || photo.s3_url || null);
    return undefined;
  }, [photo]);

  if (!src) return <div className={(className || "") + " ph-blank"} />;
  return <img className={className} src={src} alt="" onClick={onClick} />;
}

/* ------------------------------------------------------------------ */
/* per-checkpoint capture                                              */
/* ------------------------------------------------------------------ */

/**
 * The camera button that sits on a checkpoint.
 *
 * The photo list is passed in, never fetched here — the audit screen loads
 * photos once and hands each card the ones that belong to it.
 */
export function ElectricalQuestionPhotos({ photos, checkpointCode, auditId, disabled, onChange }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const input = useRef(null);

  const mine = (photos || []).filter((x) =>
    (x.checkpoint_codes || []).includes(checkpointCode)
  );

  const take = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;

    setBusy(true);
    setError(null);
    try {
      for (const f of files) {
        await api.capturePhoto(auditId, f, checkpointCode);
      }
      if (onChange) await onChange();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const detach = async (local_id) => {
    try {
      await api.unlinkPhoto(auditId, local_id, checkpointCode);
      if (onChange) await onChange();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="q-photos">
      <input
        ref={input}
        type="file"
        accept="image/*"
        capture="environment"
        multiple
        style={{ display: "none" }}
        onChange={take}
      />

      {!disabled ? (
        <button
          type="button"
          className="q-drop"
          disabled={busy}
          onClick={() => input.current && input.current.click()}
        >
          <span className="cam" aria-hidden="true">📷</span>
          <span>{busy ? "Saving…" : "Take a photo for this checkpoint"}</span>
          {mine.length ? (
            <span className="taken">
              {mine.map((p) => (
                <span className="q-photo" key={p.local_id}>
                  <PhotoImg photo={p} className="q-photo-img" />
                  {p.upload_state !== "uploaded" ? (
                    <span className="ph-pending" title="Not yet synced" />
                  ) : null}
                </span>
              ))}
            </span>
          ) : null}
        </button>
      ) : null}

      {mine.length ? (
        <div className="q-photo-row">
          {disabled
            ? mine.map((p) => (
                <div className="q-photo" key={p.local_id}>
                  <PhotoImg photo={p} className="q-photo-img" />
                </div>
              ))
            : mine.map((p) => (
                <div className="q-photo" key={p.local_id}>
                  <PhotoImg photo={p} className="q-photo-img" />
                  {p.upload_state !== "uploaded" ? (
                    <span className="ph-pending" title="Not yet synced" />
                  ) : null}
                  <button
                    type="button"
                    className="q-photo-x"
                    title="Remove from this checkpoint"
                    onClick={() => detach(p.local_id)}
                  >
                    ✕
                  </button>
                </div>
              ))}
        </div>
      ) : null}

      {error ? <div className="q-photo-err">{error}</div> : null}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* the camera roll                                                     */
/* ------------------------------------------------------------------ */

function StorageBar({ auditId, onFreed }) {
  const [room, setRoom] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const check = useCallback(() => {
    api.storageEstimate().then(setRoom).catch(() => setRoom(null));
  }, []);

  useEffect(check, [check]);

  if (!room || room.pct < 80) return null;

  const free = async () => {
    setBusy(true);
    setMsg(null);
    try {
      const r = await api.syncAndFreeSpace(auditId);
      const mb = Math.round((r.freed_bytes || 0) / (1024 * 1024));
      setMsg(
        r.uploaded
          ? `Uploaded ${r.uploaded} photo${r.uploaded === 1 ? "" : "s"} and freed ${mb} MB.`
          : mb
          ? `Freed ${mb} MB.`
          : "Nothing left to upload."
      );
      check();
      if (onFreed) await onFreed();
    } catch (e) {
      setMsg(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={"ph-quota" + (room.pct >= 90 ? " critical" : "")}>
      <div>
        <strong>This device is {room.pct}% full</strong>
        <span>
          Uploaded photos can be cleared from the device without losing them —
          they stay on the server and the thumbnails remain here.
        </span>
        {msg ? <em>{msg}</em> : null}
      </div>
      <button type="button" className="small-btn" disabled={busy} onClick={free}>
        {busy ? "Working…" : "Sync and free space"}
      </button>
    </div>
  );
}

export default function ElectricalCameraRoll({ auditId, framework, answers, readOnly, onChange }) {
  const [photos, setPhotos] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [assigning, setAssigning] = useState(null);
  const [viewing, setViewing] = useState(null);
  const input = useRef(null);

  const load = useCallback(() => {
    api
      .listPhotos(auditId)
      .then(setPhotos)
      .catch((e) => setError(e.message));
  }, [auditId]);

  useEffect(load, [load]);

  const take = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setBusy(true);
    setError(null);
    try {
      for (const f of files) await api.capturePhoto(auditId, f, null);
      load();
      if (onChange) onChange();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const assign = async (local_id, code) => {
    try {
      await api.linkPhoto(auditId, local_id, code);
      setAssigning(null);
      load();
      if (onChange) onChange();
    } catch (err) {
      setError(err.message);
    }
  };

  const discard = async (local_id) => {
    try {
      await api.deletePhoto(auditId, local_id);
      load();
      if (onChange) onChange();
    } catch (err) {
      setError(err.message);
    }
  };

  if (!photos) return <div className="cap-loading">Loading photos…</div>;

  const unassigned = photos.filter((p) => !(p.checkpoint_codes || []).length);
  const assigned = photos.filter((p) => (p.checkpoint_codes || []).length);
  const notUploaded = photos.filter((p) => p.upload_state !== "uploaded").length;

  return (
    <div className="cap-writeup">
      <input
        ref={input}
        type="file"
        accept="image/*"
        capture="environment"
        multiple
        style={{ display: "none" }}
        onChange={take}
      />

      <div className="wu-head">
        <div>
          <h2>Photos</h2>
          <p>
            Shoot as you walk and assign them here afterwards. Every photo
            must be attached to a checkpoint or discarded before this audit
            can be submitted.
          </p>
        </div>
        {!readOnly ? (
          <button
            type="button"
            className="cap-btn"
            disabled={busy}
            onClick={() => input.current && input.current.click()}
          >
            {busy ? "Saving…" : "Take photos"}
          </button>
        ) : null}
      </div>

      {error ? <p className="wu-error">{error}</p> : null}

      <StorageBar auditId={auditId} onFreed={load} />

      {notUploaded ? (
        <p className="ph-note">
          {notUploaded} photo{notUploaded === 1 ? "" : "s"} still on this device, waiting to sync.
        </p>
      ) : null}

      {photos.length === 0 ? (
        <div className="wu-empty">
          <h2>No photos yet</h2>
          <p>
            Photos taken here go into the roll unassigned. You attach them to
            checkpoints at the desk, which is faster than finding the right
            checkpoint while standing in front of the thing.
          </p>
        </div>
      ) : null}

      {unassigned.length ? (
        <>
          <h3 className="ph-section blocked">
            {unassigned.length} not yet attached — these block submit
          </h3>
          <div className="ph-grid">
            {unassigned.map((p) => (
              <div className="ph-tile" key={p.local_id}>
                <PhotoImg photo={p} className="ph-thumb" onClick={() => setViewing(p)} />
                {p.upload_state !== "uploaded" ? <span className="ph-pending" /> : null}
                {!readOnly ? (
                  <div className="ph-actions">
                    <button type="button" className="small-btn" onClick={() => setAssigning(p)}>
                      Attach
                    </button>
                    <button
                      type="button"
                      className="small-btn warn"
                      onClick={() => discard(p.local_id)}
                    >
                      Discard
                    </button>
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        </>
      ) : null}

      {assigned.length ? (
        <>
          <h3 className="ph-section">{assigned.length} attached</h3>
          <div className="ph-grid">
            {assigned.map((p) => (
              <div className="ph-tile" key={p.local_id}>
                <PhotoImg photo={p} className="ph-thumb" onClick={() => setViewing(p)} />
                {p.upload_state !== "uploaded" ? <span className="ph-pending" /> : null}
                <div className="ph-codes">
                  {(p.checkpoint_codes || []).map((c) => (
                    <span key={c}>{c}</span>
                  ))}
                </div>
                {!readOnly ? (
                  <div className="ph-actions">
                    <button type="button" className="small-btn" onClick={() => setAssigning(p)}>
                      Attach to another
                    </button>
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        </>
      ) : null}

      {assigning ? (
        <AssignDialog
          photo={assigning}
          framework={framework}
          answers={answers}
          onCancel={() => setAssigning(null)}
          onPick={(code) => assign(assigning.local_id, code)}
        />
      ) : null}

      {viewing ? <Lightbox photo={viewing} onClose={() => setViewing(null)} /> : null}
    </div>
  );
}

/* ------------------------------------------------------------------ */

/**
 * Pick a checkpoint for a photo.
 *
 * Search rather than a huge dropdown. One photo can evidence several
 * findings — a shot of a panel serves both the signage checkpoint and the
 * housekeeping one — so attaching does not replace an existing link, it adds
 * one.
 */
function AssignDialog({ photo, framework, answers, onCancel, onPick }) {
  const [q, setQ] = useState("");

  const all = [];
  if (framework) {
    (framework.areas || []).forEach((a) =>
      (a.categories || []).forEach((cat) =>
        (cat.checkpoints || []).forEach((cp) => {
          // Only checkpoints the auditor has actually answered - attaching
          // evidence to one nobody has looked at yet would leave a photo
          // linked to a finding that does not exist on the record.
          if (!(answers?.[cp.code] || {}).status) return;
          all.push({ code: cp.code, check_points: cp.check_points, category: cat.category, area: a.area });
        })
      )
    );
  }

  const needle = q.trim().toLowerCase();
  const already = photo.checkpoint_codes || [];
  const matches = (needle
    ? all.filter(
        (x) =>
          x.code.toLowerCase().includes(needle) ||
          x.check_points.toLowerCase().includes(needle) ||
          x.category.toLowerCase().includes(needle)
      )
    : all
  ).slice(0, 40);

  return (
    <>
      <div className="elec-pad-scrim" onClick={onCancel} />
      <div className="scope-dialog wide" role="dialog" aria-modal="true">
        <h3>Attach this photo</h3>
        <p>
          A photo can evidence more than one finding. The image itself is
          stored once however many checkpoints it is attached to.
        </p>

        <input
          type="text"
          autoFocus
          value={q}
          placeholder="Search by code, checkpoint or category"
          onChange={(e) => setQ(e.target.value)}
        />

        <div className="assign-list">
          {matches.map((x) => (
            <button
              type="button"
              key={x.code}
              className="assign-row"
              disabled={already.includes(x.code)}
              onClick={() => onPick(x.code)}
            >
              <span className="ar-code">{x.code}</span>
              <span className="ar-q">{x.check_points}</span>
              {already.includes(x.code) ? <span className="ar-on">attached</span> : null}
            </button>
          ))}
          {matches.length === 0 ? <p className="assign-none">Nothing matches.</p> : null}
        </div>

        <div className="scope-actions">
          <button type="button" className="small-btn" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </div>
    </>
  );
}

function Lightbox({ photo, onClose }) {
  const [src, setSrc] = useState(null);

  useEffect(() => {
    if (photo.blob) {
      const url = blobUrl(photo.blob);
      setSrc(url);
      return () => revoke(url);
    }
    setSrc(photo.s3_url || photo.thumb_url || null);
    return undefined;
  }, [photo]);

  return (
    <div className="ph-lightbox" onClick={onClose}>
      {src ? <img src={src} alt="" /> : <p>This photo is not on this device.</p>}
    </div>
  );
}
